// src/components/DocumentGuideLines.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaSearch,
  FaDownload,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAlt,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaSync,
  FaArrowLeft,
  FaArrowRight,
  FaTh,
  FaList,
} from "react-icons/fa";
import ScrollToTop from '../scrollToTop/ScrollToTop';

const DocumentGuideLines = () => {
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [showAll, setShowAll] = useState(false);
  const itemsPerPage = 6;

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/doc`
        );
        setDocuments(response.data.data || []);
      } catch (err) {
        setError("Failed to load documents. Please try again later.");
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Get unique document types for filter dropdown
  const documentTypes = [...new Set(documents.map((doc) => doc.type))];

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === "all" || doc.type === filter;
    return matchesSearch && matchesFilter;
  });

  const indexOfLastItem = showAll
    ? filteredDocuments.length
    : currentPage * itemsPerPage;
  const indexOfFirstItem = showAll ? 0 : indexOfLastItem - itemsPerPage;
  const currentItems = showAll
    ? filteredDocuments
    : filteredDocuments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);

  const getFileIcon = (file) => {
    if (!file) return <FaFileAlt className="text-gray-500 text-xl" />;

    const extension = file.split(".").pop().toLowerCase();

    switch (extension) {
      case "pdf":
        return <FaFilePdf className="text-red-500 text-xl" />;
      case "doc":
      case "docx":
        return <FaFileWord className="text-blue-500 text-xl" />;
      case "xls":
      case "xlsx":
        return <FaFileExcel className="text-green-500 text-xl" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FaFileImage className="text-yellow-500 text-xl" />;
      default:
        return <FaFileAlt className="text-gray-500 text-xl" />;
    }
  };

  const getTypeBadge = (type) => {
    if (!type) return null;

    // Capitalize the first letter of the type
    const formattedType = type.charAt(0).toUpperCase() + type.slice(1);

    // Generate consistent color based on type
    const typeColors = {
      policy: "bg-blue-100 text-blue-800",
      guideline: "bg-purple-100 text-purple-800",
      report: "bg-green-100 text-green-800",
      template: "bg-amber-100 text-amber-800",
      checklist: "bg-teal-100 text-teal-800",
      framework: "bg-rose-100 text-rose-800",
      procedure: "bg-indigo-100 text-indigo-800",
      standard: "bg-pink-100 text-pink-800",
    };

    const colorClass =
      typeColors[type.toLowerCase()] || "bg-gray-100 text-gray-800";

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${colorClass}`}
      >
        {formattedType}
      </span>
    );
  };

  const toggleExpand = (id) => {
    setExpandedDoc(expandedDoc === id ? null : id);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "grid" ? "list" : "grid");
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    if (!showAll) {
      setCurrentPage(1);
    }
  };

  const refreshDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/doc`
      );
      setDocuments(response.data.data || []);
    } catch (err) {
      setError("Failed to refresh documents. Please try again later.");
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (filePath) => {
    window.open(`${process.env.REACT_APP_API_URL}/${filePath}`, "_blank");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  const renderFormattedContent = (html) => {
    return (
      <div
        className="ql-editor"
        style={{
          padding: 0,
          fontSize: "1rem",
          lineHeight: "1.5",
          color: "#4b5563",
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  };
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (<>
       <ScrollToTop/>

    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Document Guidelines
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Access official documents, templates, and guidelines
          </p>
          <div className="w-16 h-1 bg-blue-500 mx-auto mt-4 rounded-full"></div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="relative w-full md:w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaFilter className="text-gray-400" />
                </div>
                <select
                  className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {documentTypes.map((type, index) => (
                    <option key={index} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <FaChevronDown className="text-gray-400 text-sm" />
                </div>
              </div>

              <button
                className="flex items-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"
                onClick={toggleViewMode}
              >
                {viewMode === "grid" ? <FaList /> : <FaTh />}
                <span>{viewMode === "grid" ? "List View" : "Grid View"}</span>
              </button>

              <button
                className="flex items-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"
                onClick={refreshDocuments}
              >
                <FaSync />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <p className="text-gray-600">
              Showing{" "}
              <span className="font-semibold">{filteredDocuments.length}</span>{" "}
              documents
              {searchTerm && ` matching "${searchTerm}"`}
            </p>

            <div className="flex items-center gap-3">
              <button
                className={`px-4 py-2 rounded-lg ${
                  showAll
                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                onClick={toggleShowAll}
              >
                {showAll ? "Show Less" : "Show All"}
              </button>

              <div className="flex gap-2">
                <button
                  className={`px-3 py-2 rounded-lg flex items-center ${
                    currentPage === 1
                      ? "bg-gray-200 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                >
                  <FaArrowLeft />
                </button>
                <button
                  className={`px-3 py-2 rounded-lg flex items-center ${
                    currentPage >= totalPages
                      ? "bg-gray-200 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage >= totalPages}
                >
                  <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        </div>

       

        {/* Document Grid/List */}
        {loading ? (
          <div
            className={`${
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                : "space-y-6"
            }`}
          >
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-sm overflow-hidden animate-pulse ${
                  viewMode === "list" ? "flex items-start p-6" : "p-5"
                }`}
              >
                {viewMode === "list" && (
                  <div className="bg-gray-200 rounded-lg w-16 h-16 mr-4"></div>
                )}
                <div className="flex-1">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
                  <div className="space-y-2 mb-6">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : currentItems.length > 0 ? (
          <>
            <div
              className={
                viewMode === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  : "space-y-6"
              }
            >
              {currentItems.map((doc) => (
                <div
                  key={doc.id}
                  className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all hover:shadow-md ${
                    viewMode === "list"
                      ? "flex flex-col md:flex-row items-start p-6"
                      : "p-5"
                  }`}
                >
                  {viewMode === "list" && (
                    <div className="bg-gray-100 p-4 rounded-lg mb-4 md:mb-0 md:mr-6">
                      {getFileIcon(doc.file)}
                    </div>
                  )}

                  <div className={`${viewMode === "list" ? "flex-1" : ""}`}>
                    <div
                      className={`flex justify-between items-start ${
                        viewMode === "list" ? "mb-3" : "mb-3"
                      }`}
                    >
                      <div>
                        <h3 className="font-bold text-gray-800 mb-2 text-lg">
                          {doc.title}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          {getTypeBadge(doc.type)}
                          <span className="text-xs text-gray-500">
                            {formatDate(doc.createdAt)}
                          </span>
                        </div>
                      </div>
                      {viewMode === "grid" && (
                        <div className="bg-gray-100 p-2 rounded-lg">
                          {getFileIcon(doc.file)}
                        </div>
                      )}
                    </div>

                    <div>
                      {expandedDoc === doc.id ? (
                        <div className="text-gray-600 text-sm">
                          {renderFormattedContent(doc.description)}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm">
                          {/* strip HTML tags for preview */}
                          {stripHtml(doc.description).slice(0, 250)}...
                        </p>
                      )}

                      {doc.description &&
                        stripHtml(doc.description).length > 250 && (
                          <button
                            className="text-blue-500 text-sm font-medium mt-1 flex items-center gap-1"
                            onClick={() => toggleExpand(doc.id)}
                          >
                            {expandedDoc === doc.id ? (
                              <>
                                <span>Show less</span>
                                <FaChevronUp className="text-xs" />
                              </>
                            ) : (
                              <>
                                <span>Read more</span>
                                <FaChevronDown className="text-xs" />
                              </>
                            )}
                          </button>
                        )}
                    </div>

                    <div
                      className={`flex justify-between items-center mt-4 pt-4 border-t border-gray-100 ${
                        viewMode === "list"
                          ? "flex-col md:flex-row gap-4 md:gap-0"
                          : ""
                      }`}
                    >
                      <div className="text-xs text-gray-500 truncate max-w-[60%]">
                        {doc.file?.split("/").pop()}
                      </div>
                      <button
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                        onClick={() => handleDownload(doc.file)}
                      >
                        <FaDownload />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {!showAll && totalPages > 1 && (
              <div className="flex justify-center mt-10">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-sm">
                  <button
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentPage === 1
                        ? "text-gray-400"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    <FaArrowLeft />
                  </button>

                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        currentPage === index + 1
                          ? "bg-blue-500 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}

                  <button
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentPage === totalPages
                        ? "text-gray-400"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="text-6xl text-gray-300 mb-4">📄</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {documents.length === 0
                  ? "No documents available"
                  : "No matching documents found"}
              </h3>
              <p className="text-gray-500 mb-6">
                {documents.length === 0
                  ? "Documents will appear here once they are added to the system."
                  : "Try adjusting your search or filter criteria."}
              </p>
              <button
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                onClick={() => {
                  setSearchTerm("");
                  setFilter("all");
                }}
              >
                {documents.length === 0 ? "Refresh" : "Clear Filters"}
              </button>
            </div>
          </div>
        )}

        {/* Stats Footer */}
        {/* <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-600">
            {documents.length} documents in total • Last updated: {new Date().toLocaleDateString()}
          </p>
        </div> */}
      </div>
    </div>
    </>
    
  );
};

export default DocumentGuideLines;
